import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import SimpleTable from '../../components/super/SimpleTable';
import { useStores } from '../../hooks/use-stores';
import {
    DeliveryStatusChip,
    TestChip,
    preStyle,
} from '../../components/super/webhooks/webhookFormatters';

function Field({ label, children }) {
    return (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="textSecondary">
                {label}
            </Typography>
            <Typography variant="body1" style={{ wordBreak: 'break-all' }}>
                {children ?? '-'}
            </Typography>
        </Grid>
    );
}

/** Local datetime string for an <input type="datetime-local"> default. */
const toLocalInput = (value) =>
    moment(value || undefined)
        .add(value ? 0 : 15, 'minutes')
        .format('YYYY-MM-DDTHH:mm');

function WebhookEventView() {
    const { eventId } = useParams();
    const { webhooksStore } = useStores();
    const { event, loadingEvent, runningEvent, reschedulingEvent, error } =
        webhooksStore;
    const navigate = useNavigate();

    const [nextTry, setNextTry] = useState('');

    useEffect(() => {
        webhooksStore.loadEvent(eventId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    useEffect(() => {
        if (event) setNextTry(toLocalInput(event.next_try));
    }, [event]);

    // Attempts already made. Mirrors the scheduled-task detail: the row that is
    // still waiting is shown above the table, since it has no execution record
    // of its own yet.
    const executionColumns = useMemo(
        () => [
            {
                Header: '#',
                id: 'attempt',
                accessor: (row, index) => index + 1,
            },
            {
                Header: 'At',
                accessor: 'created',
                Cell: (x) => (x.value ? moment(x.value).format('LLL') : '-'),
            },
            {
                Header: 'HTTP',
                accessor: 'status',
                Cell: (x) => (x.value === 0 ? 'no response' : x.value),
            },
            {
                Header: 'Outcome',
                id: 'outcome',
                accessor: (row) =>
                    row.status >= 200 && row.status < 300
                        ? 'Accepted'
                        : 'Rejected',
            },
            {
                Header: 'Response',
                accessor: 'response',
                Cell: (x) => (
                    <span style={{ wordBreak: 'break-all' }}>
                        {(x.value || '-').toString().slice(0, 120)}
                    </span>
                ),
                disableSortBy: true,
            },
        ],
        []
    );

    if (loadingEvent || !event) return <ThemedSpinner />;

    const actions = event.actions || {};
    const executions = event.executions || [];
    const isWaiting = !!event.next_try;

    const runNow = async () => {
        await webhooksStore.runEventNow(eventId);
    };

    const reschedule = async () => {
        if (!nextTry) return;
        await webhooksStore.rescheduleEvent(
            eventId,
            new Date(nextTry).toISOString()
        );
    };

    return (
        <Grid container direction="row" spacing={2}>
            <Grid item xs={12}>
                <Box
                    mt={4}
                    mb={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                        <Typography variant="h2">
                            Event #{event.id} {event.eventName}
                        </Typography>
                        <DeliveryStatusChip status={event.delivery_status} />
                        <TestChip isTest={event.is_test} />
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/webhookEvents')}
                    >
                        All events
                    </Button>
                </Box>

                {error && (
                    <Box mb={2}>
                        <Alert severity="error">
                            {(error.response &&
                                error.response.data &&
                                error.response.data.error) ||
                                error.message ||
                                'Request failed'}
                        </Alert>
                    </Box>
                )}

                <Paper style={{ padding: 16, textAlign: 'left' }}>
                    <Grid container spacing={2}>
                        <Field label="Webhook">
                            {event.webhook ? (
                                <Link to={`/webhooks/${event.webhook.id}`}>
                                    #{event.webhook.id} {event.webhook.name}
                                </Link>
                            ) : (
                                'deleted'
                            )}
                        </Field>
                        <Field label="Url">{event.webhook?.url}</Field>
                        <Field label="Last HTTP status">
                            {event.status === 0 ? 'no response' : event.status}
                        </Field>
                        <Field label="Attempts">{event.attempts}</Field>
                        <Field label="Next try">
                            {event.next_try
                                ? moment(event.next_try).format('LLL')
                                : '-'}
                        </Field>
                        <Field label="Company id">{event.companyId}</Field>
                        <Field label="Partner id">{event.partnerId}</Field>
                        <Field label="Unique id">{event.unique_id}</Field>
                        <Field label="Created">
                            {moment(event.created).format('LLL')}
                        </Field>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                                Payload
                            </Typography>
                            <pre style={preStyle}>
                                {JSON.stringify(
                                    event.definition && event.definition.data,
                                    null,
                                    2
                                )}
                            </pre>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* The pending attempt has no execution row yet, so it is shown
                above the table - the same shape as a scheduled task's next run. */}
            <Grid item xs={12}>
                <Paper style={{ padding: 16, textAlign: 'left' }}>
                    <Typography variant="h3" gutterBottom>
                        Next attempt
                    </Typography>

                    {!actions.canRunNow && !actions.canReschedule ? (
                        <Typography variant="body2" color="textSecondary">
                            {actions.reason || 'Nothing further is scheduled.'}
                        </Typography>
                    ) : (
                        <Box
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                            style={{ gap: 16 }}
                        >
                            <Typography variant="body2">
                                {isWaiting
                                    ? `Due ${moment(event.next_try).format('LLL')} (attempt ${
                                          event.attempts + 1
                                      })`
                                    : 'Waiting to be picked up'}
                            </Typography>

                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={runNow}
                                disabled={!actions.canRunNow || runningEvent}
                            >
                                {runningEvent ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    'Run Now'
                                )}
                            </Button>

                            {actions.canReschedule && (
                                <>
                                    <TextField
                                        label="Move next attempt to"
                                        type="datetime-local"
                                        size="small"
                                        value={nextTry}
                                        onChange={(e) =>
                                            setNextTry(e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={reschedule}
                                        disabled={reschedulingEvent}
                                    >
                                        {reschedulingEvent ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            'Reschedule'
                                        )}
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}

                    {actions.reason &&
                        (actions.canRunNow || actions.canReschedule) && (
                            <Box mt={1}>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    {actions.reason}
                                </Typography>
                            </Box>
                        )}
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Box mt={2} mb={2}>
                    <Typography variant="h3" align="left">
                        Attempts ({executions.length})
                    </Typography>
                </Box>
                {executions.length > 0 ? (
                    <SimpleTable
                        columns={executionColumns}
                        data={executions}
                        pageSize={10}
                    />
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        No delivery attempted yet.
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
}

export default observer(WebhookEventView);
